import isEmpty from "lodash/isEmpty";
import keyBy from "lodash/keyBy";
import mapValues from "lodash/mapValues";
import max from "lodash/max";
import mergeWith from "lodash/mergeWith";
import uniq from "lodash/uniq";
import {
  BookingModuleStoreType,
  CalendarRootStoreSlice,
  CalendarRootStoreSliceActions,
  DEFAULT_PAGE,
  RowsWithRefs
} from "PFApp/booking/store";
import { resetRowChildrenPagination } from "PFApp/booking/store/provider/get_calendar_root_store_slice_providers.utils";
import { Row } from "PFApp/booking/types";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { EmptyObject } from "type-fest";
import { StateCreator } from "zustand";

type CalendarRootStateCreator = () => CalendarRootStoreSlice<EmptyObject, EmptyObject>;
type CalendarRootStateActionsCreator = StateCreator<
  BookingModuleStoreType,
  [],
  [],
  CalendarRootStoreSliceActions<EmptyObject, EmptyObject>
>;

type GetCalendarRootStoreSliceProviders<T extends CalendarRoot> = {
  calendarRoot: T;
};
type GetCalendarRootStoreSliceProvidersReturn = {
  createCalendarRootStoreSlice: CalendarRootStateCreator;
  createCalendarRootStoreSliceActions: CalendarRootStateActionsCreator;
};

export const getCalendarRootStoreSliceProviders = <T extends CalendarRoot>({
  calendarRoot
}: GetCalendarRootStoreSliceProviders<T>): GetCalendarRootStoreSliceProvidersReturn => {
  const createCalendarRootStoreSlice: CalendarRootStateCreator = () => ({
    pinnedIds: [],
    page: 1,
    totalPages: 0,
    order: {
      selectedOrder: null,
      options: []
    },
    columns: {
      profiles: {
        availableColumns: [],
        selectedColumns: ["root"],
        inlineColumns: []
      },
      engagements: {
        availableColumns: [],
        selectedColumns: ["root"],
        inlineColumns: []
      },
      roles: {
        availableColumns: [],
        selectedColumns: ["root"],
        inlineColumns: []
      }
    },
    counters: null,

    rows: [],
    rowsByKey: {},
    rowRefs: new Map(),
    expandedRowsState: {},
    rowsLoadingByKey: {},
    bookingsByRowKey: {},
    warningsByProfile: {},
    childrenInPastByRowKey: {},
    paginationByRowKey: {},
    barsByRowKey: {}
  });

  const createCalendarRootStoreSliceActions: CalendarRootStateActionsCreator = (set, get) => ({
    addPinnedId: (id) => {
      const calendarRootSlice = get()[calendarRoot];
      const { pinnedIds } = calendarRootSlice;
      set({ [calendarRoot]: { ...calendarRootSlice, pinnedIds: uniq([...pinnedIds, id]) } });
    },
    removePinnedId: (id) => {
      const { pinnedIds, rows } = get()[calendarRoot];
      const row = rows.find((row) => row.original.id === id);
      if (row) {
        get().actions[calendarRoot].setExpandedForRow(row.key, false);
      }
      set({
        [calendarRoot]: {
          ...get()[calendarRoot],
          pinnedIds: pinnedIds.filter((pinnedId) => pinnedId !== id)
        }
      });
    },
    clearPinnedIds: () => {
      const { rows, pinnedIds } = get()[calendarRoot];
      const pinnedRows = rows.filter((row) => pinnedIds.includes(row.original.id as number));
      if (!isEmpty(pinnedRows)) {
        pinnedRows.forEach((row) => get().actions[calendarRoot].setExpandedForRow(row.key, false));
      }
      set({ [calendarRoot]: { ...get()[calendarRoot], pinnedIds: [] } });
    },
    setPage: (page) => {
      const calendarRootSlice = get()[calendarRoot];
      set({ [calendarRoot]: { ...calendarRootSlice, page } });
    },
    setTotalPages: (totalPages) => {
      const calendarRootSlice = get()[calendarRoot];
      set({ [calendarRoot]: { ...calendarRootSlice, totalPages } });
    },
    resetPagination: () => {
      const { setPage } = get().actions[calendarRoot];
      setPage(DEFAULT_PAGE);
    },
    setAvailableColumns: (columnsType, availableColumns) => {
      const calendarRootSlice = get()[calendarRoot];
      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          columns: {
            ...calendarRootSlice.columns,
            [columnsType]: {
              ...calendarRootSlice.columns[columnsType],
              availableColumns
            }
          }
        }
      });
    },
    setColumnsDisplay: (columnsType, columnsDisplay) => {
      const calendarRootSlice = get()[calendarRoot];
      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          columns: {
            ...calendarRootSlice.columns,
            [columnsType]: {
              ...calendarRootSlice.columns[columnsType],
              ...columnsDisplay
            }
          }
        }
      });
    },
    setAvailableOrderOptions: (options) => {
      const calendarRootSlice = get()[calendarRoot];
      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          order: {
            ...calendarRootSlice.order,
            options
          }
        }
      });
    },
    setSelectedOrder: (selectedOrder) => {
      const calendarRootSlice = get()[calendarRoot];
      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          order: {
            ...calendarRootSlice.order,
            selectedOrder
          }
        }
      });
    },
    setCounters: (counters) => {
      const calendarRootSlice = get()[calendarRoot];
      set({ [calendarRoot]: { ...calendarRootSlice, counters } });
    },
    setRows: (rows) => {
      const calendarRootSlice = get()[calendarRoot];
      const rowsByKey = keyBy(rows, "key") as Record<string, Row>;
      set({ [calendarRoot]: { ...calendarRootSlice, rows, rowsByKey } });
    },
    setRowRef: (rowKey, element) => {
      // mutates the existing Map instance in place without replacing it to not trigger rerenders.
      const { rowRefs } = get()[calendarRoot];
      const prevNode = rowRefs.get(rowKey);
      if (prevNode === element) {
        return;
      }
      if (element) {
        rowRefs.set(rowKey, element);
      } else {
        rowRefs.delete(rowKey);
      }
    },
    getRowsWithRefs: () => {
      const { rowRefs, rowsByKey } = get()[calendarRoot];
      const rowsWithRefs: RowsWithRefs<Row> = new Map();
      rowRefs.forEach((rowRef, key) => {
        const row = rowsByKey[key];
        if (row) {
          rowsWithRefs.set(key, { row, rowRef });
        }
      });
      return rowsWithRefs;
    },
    setExpandedRowsState: (expandedRowsState) => {
      const calendarRootSlice = get()[calendarRoot];
      set({ [calendarRoot]: { ...calendarRootSlice, expandedRowsState } });
    },
    setExpandedForRow: (rowKey, expanded) => {
      const calendarRootSlice = get()[calendarRoot];

      // when collapsing the row, reset it and its children pagination state
      const updatedPaginationByRowKey = expanded
        ? calendarRootSlice.paginationByRowKey
        : resetRowChildrenPagination(calendarRootSlice.paginationByRowKey, rowKey);

      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          expandedRowsState: { ...calendarRootSlice.expandedRowsState, [rowKey]: expanded },
          paginationByRowKey: updatedPaginationByRowKey
        }
      });
    },
    setRowsLoadingByKey: (rowsLoadingByKey, loadingKey) => {
      const calendarRootSlice = get()[calendarRoot];
      // update `loadingKey` state with `rowsLoadingByKey`
      const loadingState = mergeWith(
        {},
        calendarRootSlice.rowsLoadingByKey,
        rowsLoadingByKey,
        (state, newState) => ({
          ...state,
          ...newState
        })
      );
      // reset state for `loadingKey` in case the row is not in `rowsLoadingByKey`
      const updatedLoading = mapValues(loadingState, (value, key) =>
        !rowsLoadingByKey[key] ? { ...value, [loadingKey]: false } : value
      );
      set({ [calendarRoot]: { ...calendarRootSlice, rowsLoadingByKey: updatedLoading } });
    },
    setBookingsByRowKey: (bookingsByRowKey) => {
      const calendarRootSlice = get()[calendarRoot];
      set({ [calendarRoot]: { ...calendarRootSlice, bookingsByRowKey } });
    },
    setWarningsByProfile: (warningsByProfile) => {
      const calendarRootSlice = get()[calendarRoot];
      set({ [calendarRoot]: { ...calendarRootSlice, warningsByProfile } });
    },
    setChildrenInPastEnabled: (rowKey, enabled) => {
      const calendarRootSlice = get()[calendarRoot];
      const rowKeyData = calendarRootSlice.childrenInPastByRowKey[rowKey];
      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          childrenInPastByRowKey: {
            ...calendarRootSlice.childrenInPastByRowKey,
            [rowKey]: { ...rowKeyData, enabled }
          }
        }
      });
    },
    setHasChildrenInPastByRowKey: (hasChildrenInPastByRowKey) => {
      const calendarRootSlice = get()[calendarRoot];
      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          childrenInPastByRowKey: mapValues(calendarRootSlice.childrenInPastByRowKey, (entry, key) => ({
            ...entry,
            hasChildrenInPast: hasChildrenInPastByRowKey[key] ?? entry.hasChildrenInPast
          }))
        }
      });
    },
    resetHasChildrenInPast: () => {
      const calendarRootSlice = get()[calendarRoot];
      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          childrenInPastByRowKey: {}
        }
      });
    },
    setPageForRow: (rowKey, page) => {
      const calendarRootSlice = get()[calendarRoot];
      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          paginationByRowKey: {
            ...calendarRootSlice.paginationByRowKey,
            [rowKey]: { ...calendarRootSlice.paginationByRowKey[rowKey], page }
          }
        }
      });
    },
    setPaginationMetaByRowKey: (metaByRowKey) => {
      const calendarRootSlice = get()[calendarRoot];
      const updatedPagination = mapValues(metaByRowKey, (metaByChildrenType, key) => {
        const childrenTypeMetas = Object.values(metaByChildrenType);
        const maxTotalPages = max(childrenTypeMetas.map(({ totalPages }) => totalPages)) ?? 0;
        const maxTotal = max(childrenTypeMetas.map(({ total }) => total)) ?? 0;
        const currentPage = calendarRootSlice.paginationByRowKey[key]?.page ?? 1;
        const page = currentPage > maxTotalPages ? (maxTotalPages ?? 1) : currentPage;
        return {
          ...calendarRootSlice.paginationByRowKey[key],
          total: maxTotal,
          totalPages: maxTotalPages,
          metaByChildrenType,
          page
        };
      });
      set({
        [calendarRoot]: {
          ...calendarRootSlice,
          paginationByRowKey: {
            ...calendarRootSlice.paginationByRowKey,
            ...updatedPagination
          }
        }
      });
    },
    setPaginationByRowKey: (paginationByRowKey) => {
      const calendarRootSlice = get()[calendarRoot];
      set({ [calendarRoot]: { ...calendarRootSlice, paginationByRowKey } });
    },
    setBarsByRowKey: (barsByRowKey) => {
      const calendarRootSlice = get()[calendarRoot];
      set({ [calendarRoot]: { ...calendarRootSlice, barsByRowKey } });
    }
  });

  return { createCalendarRootStoreSlice, createCalendarRootStoreSliceActions };
};
